import type React from 'react';
import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ui } from '../config/uiSizes';

interface TooltipProps {
  content: string | React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  variant?: 'default' | 'amber';
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  position = 'top',
  className = '',
  variant = 'default'
}) => {
  const [visible, setVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const GAP = 8;
    let style: React.CSSProperties = { position: 'fixed', zIndex: 9999 };

    switch (position) {
      case 'right':
        style = { ...style, top: rect.top + rect.height / 2, left: rect.right + GAP, transform: 'translateY(-50%)' };
        break;
      case 'left':
        style = { ...style, top: rect.top + rect.height / 2, right: window.innerWidth - rect.left + GAP, transform: 'translateY(-50%)' };
        break;
      case 'top':
        style = { ...style, bottom: window.innerHeight - rect.top + GAP, left: rect.left + rect.width / 2, transform: 'translateX(-50%)' };
        break;
      case 'bottom':
        style = { ...style, top: rect.bottom + GAP, left: rect.left + rect.width / 2, transform: 'translateX(-50%)' };
        break;
    }

    setTooltipStyle(style);
  }, [position]);

  // After the panel renders, clamp it inside the viewport if it overflows
  useEffect(() => {
    if (!visible || !panelRef.current) return;
    const rect = panelRef.current.getBoundingClientRect();
    const MARGIN = 8;
    let adjustment = 0;

    if (rect.bottom > window.innerHeight - MARGIN) {
      adjustment = rect.bottom - (window.innerHeight - MARGIN);
    } else if (rect.top < MARGIN) {
      adjustment = rect.top - MARGIN;
    }

    if (adjustment !== 0) {
      setTooltipStyle((prev) => ({
        ...prev,
        top: typeof prev.top === 'number' ? prev.top - adjustment : prev.top
      }));
    }
  }, [visible]);

  const showTooltip = useCallback(() => {
    calculatePosition();
    setVisible(true);
  }, [calculatePosition]);

  const hideTooltip = useCallback(() => setVisible(false), []);

  const isAmber = variant === 'amber';

  const panelBg = isAmber
    ? 'bg-amber-50 dark:bg-amber-950'
    : 'bg-white dark:bg-gray-800';
  const panelBorder = isAmber
    ? 'border-amber-400 dark:border-amber-500'
    : 'border-blue-500 dark:border-blue-400';
  const textColor = isAmber
    ? 'text-amber-800 dark:text-amber-200'
    : 'text-gray-700 dark:text-gray-300';
  const triggerHover = isAmber
    ? 'hover:border-amber-500 hover:text-amber-600 dark:hover:border-amber-400 dark:hover:text-amber-400'
    : 'hover:border-blue-500 hover:text-blue-600 dark:hover:border-blue-400 dark:hover:text-blue-400';
  const triggerBase = isAmber
    ? 'border-gray-400 dark:border-gray-500 text-gray-500 dark:text-gray-400'
    : 'border-gray-400 dark:border-gray-500 text-gray-500 dark:text-gray-400';

  const arrowClass = {
    top: `top-full left-1/2 -translate-x-1/2 ${isAmber ? 'border-t-amber-50 dark:border-t-amber-950' : 'border-t-white dark:border-t-gray-800'}`,
    bottom: `bottom-full left-1/2 -translate-x-1/2 ${isAmber ? 'border-b-amber-50 dark:border-b-amber-950' : 'border-b-white dark:border-b-gray-800'}`,
    left: `left-full top-1/2 -translate-y-1/2 ${isAmber ? 'border-l-amber-50 dark:border-l-amber-950' : 'border-l-white dark:border-l-gray-800'}`,
    right: `right-full top-1/2 -translate-y-1/2 ${isAmber ? 'border-r-amber-50 dark:border-r-amber-950' : 'border-r-white dark:border-r-gray-800'}`,
  };

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      {/* Information Icon */}
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        className={`flex items-center justify-center ${ui.widthAndHeight.tooltipTrigger} ml-1.5 rounded-full border-2 ${triggerBase} ${triggerHover} transition-all duration-200 cursor-default`}
        aria-label="More information"
      >
        <svg
          className={ui.svg.xxs}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={3}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v.01M12 12v6"
          />
        </svg>
      </button>

      {/* Tooltip Content — rendered in a portal to escape overflow-hidden ancestors */}
      {visible &&
        createPortal(
          <div
            ref={panelRef}
            style={tooltipStyle}
            className={`
              ${ui.widthAndHeight.tooltipPanel}
              ${panelBg}
              border-2 ${panelBorder}
              rounded-lg shadow-2xl
              pointer-events-none
              animate-in fade-in duration-200
            `}
          >
            {/* Content */}
            <div className={`${ui.page.smPadding}`}>
              <div className={`${ui.text.body} ${textColor} leading-relaxed`}>
                {content}
              </div>
            </div>

            {/* Tooltip Arrow */}
            <div
              className={`
                absolute ${arrowClass[position]}
                w-0 h-0
                border-[6px] border-transparent
              `}
            />
            {/* Arrow border for the outline effect */}
            <div
              className={`
                absolute ${arrowClass[position]}
                w-0 h-0
                border-[7px] border-transparent
                ${position === 'top' ? `${isAmber ? 'border-t-amber-400 dark:border-t-amber-500' : 'border-t-gray-300 dark:border-t-gray-600'} translate-y-[2px]` : ''}
                ${position === 'bottom' ? `${isAmber ? 'border-b-amber-400 dark:border-b-amber-500' : 'border-b-gray-300 dark:border-b-gray-600'} -translate-y-[2px]` : ''}
                ${position === 'left' ? `${isAmber ? 'border-l-amber-400 dark:border-l-amber-500' : 'border-l-gray-300 dark:border-l-gray-600'} translate-x-[2px]` : ''}
                ${position === 'right' ? `${isAmber ? 'border-r-amber-400 dark:border-r-amber-500' : 'border-r-gray-300 dark:border-r-gray-600'} -translate-x-[2px]` : ''}
              `}
            />
          </div>,
          document.body
        )}
    </div>
  );
};
